import "./VerifyBusinessUser.scss"
import { useState, useEffect, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Card,
  CardBody,
  Col,
  Row,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { ProgressSpinner } from "primereact/progressspinner"
import defaultAvatar from "assets/images/man.png"
import "flag-icons/css/flag-icons.min.css"
import { Toast } from "primereact/toast"
import { Accordion, AccordionTab } from "primereact/accordion"
import InstituteApi from "Api/InstituteApi"



const VerifyBusinessUser = () => {
  const navigate = useNavigate()
  const { businessId } = useParams()
  const accessToken = localStorage.getItem("accessToken")
  // const [isEditing, setIsEditing] = useState(false) // Commented out unused state
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState("Document")
  const [userData, setUserData] = useState(null)
  const toast = useRef(null)
  const [documentModal, setDocumentModal] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [verificationMessage, setVerificationMessage] = useState("")

  console.log(businessId)

  const breadcrumbItems = [
    { label: "Home", link: "/" },
    { label: "Users", link: "/user-list" },
    { label: "Verify Business User", link: "#" },
  ]

  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  const sidebarItems = [
    { icon: "document", label: "Documents", id: "Document" },
  ]

  const renderSidebar = () => (
    <Card className="h-100">
      <CardBody>
        <div className="text-center">
          <div className="profile-image mb-4">
            <img
              src={userData?.profilePicture || defaultAvatar}
              alt="Profile"
              className="rounded-circle img-thumbnail"
              width="120"
            />
          </div>
          <h4>{userData?.businessName}</h4>
          <p className="text-muted">{userData?.userType}</p>
        </div>

        <div className="profile-navigation mt-4">
          {sidebarItems.map(item => (
            <div
              key={item.id}
              className={`nav-item d-flex align-items-center p-3 ${activeTab === item.id ? "active" : ""}`}
              onClick={() => setActiveTab(item.id)}
              style={{ cursor: "pointer" }}
            >
              <i className={`ion ion-${item.icon} me-2`}></i>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case "Document":
        return renderDocuments()
      default:
        return renderDocuments()
    }
  }

  useEffect(() => {
    const fetchBusinessUserData = async () => {
      try {
        setLoading(true)
        const config = {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
        const response = await InstituteApi.getInstituteById(businessId, config)
        setUserData(response.data)
      } catch (error) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch business user data",
        })
      } finally {
        setLoading(false)
      }
    }

    if (businessId) {
      fetchBusinessUserData()
    }
  }, [businessId, accessToken])

  const handleDocumentView = document => {
    setSelectedDocument(document)
    setDocumentModal(true)
  }

  const handleDocumentVerification = async status => {
    try {
      if (status === "REJECTED" && !verificationMessage.trim()) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Please provide a reason for rejection",
        })
        return
      }

      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }

      const data = {
        documentName: selectedDocument.documentName,
        status,
        message: verificationMessage,
        documentType: selectedDocument.documentType || "SUPPORTING",
      }

      await InstituteApi.verifyInstitutionDocument(businessId, data, config)

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Document verification status updated successfully",
      })

      // Refresh user data
      const response = await InstituteApi.getInstituteById(businessId, config)
      setUserData(response.data)

      setDocumentModal(false)
      setVerificationMessage("")
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail:
          error.response?.data?.message ||
          "Failed to update document verification status",
      })
    }
  }

  const handleVerifyBusinessUser = async () => {
    try {
      setLoading(true)
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }

      await InstituteApi.verifyBusinessUser(businessId, {}, config)

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Business user verified successfully",
      })

      const response = await InstituteApi.getInstituteById(businessId, config)
      setUserData(response.data)
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail:
          error.response?.data?.message || "Failed to verify business user",
      })
    } finally {
      setLoading(false)
    }
  }

  const areAllDocumentsVerified = () => {
    if (!userData?.verificationDetails) return false

    const { supportingDocuments, companyLegalDocuments } =
      userData.verificationDetails

    const isSupportingDocumentsVerified =
      supportingDocuments?.every(doc => doc.status === "VERIFIED") || false

    const isLegalDocumentsVerified =
      companyLegalDocuments?.every(doc => doc.status === "VERIFIED") || false

    return isSupportingDocumentsVerified && isLegalDocumentsVerified
  }

  const renderDocumentModal = () => (
    <Modal
      isOpen={documentModal}
      toggle={() => setDocumentModal(false)}
      size="lg"
    >
      <ModalHeader toggle={() => setDocumentModal(false)}>
        {selectedDocument?.documentName}
      </ModalHeader>
      <ModalBody>
        <div className="document-viewer">
          {selectedDocument?.documentUrl ? (
            <div style={{ height: "600px", width: "100%", overflow: "auto" }}>
              <iframe
                src={selectedDocument?.documentUrl}
                title={selectedDocument.documentName}
                width="100%"
                height="100%"
                style={{ border: "none" }}
              />
              <div className="text-center mt-3">
                <Button
                  color="primary"
                  onClick={() =>
                    window.open(selectedDocument.documentUrl, "_blank")
                  }
                >
                  Download PDF
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center p-4">
              <p>No document URL provided</p>
            </div>
          )}
        </div>
        <div className="verification-form mt-4">
          <Input
            type="textarea"
            placeholder="Enter verification message (required for rejection)"
            value={verificationMessage}
            onChange={e => setVerificationMessage(e.target.value)}
            className="mb-3"
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => setDocumentModal(false)}>
          Close
        </Button>
        <Button
          color="success"
          onClick={() => handleDocumentVerification("VERIFIED")}
        >
          Approve
        </Button>
        <Button
          color="danger"
          onClick={() => handleDocumentVerification("REJECTED")}
        >
          Reject
        </Button>
      </ModalFooter>
    </Modal>
  )

  const renderDocuments = () => {
    if (!userData?.verificationDetails) return null

    const { supportingDocuments, companyLegalDocuments } =
      userData.verificationDetails

    return (
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4>Verification Documents</h4>
            <Button
              color="success"
              onClick={handleVerifyBusinessUser}
              disabled={
                !areAllDocumentsVerified() || userData?.status === "VERIFIED"
              }
            >
              Verify Business User
            </Button>
          </div>

          <Accordion>
            <AccordionTab header="Supporting Documents">
              {supportingDocuments.map((doc, index) => (
                <div key={doc._id} className="mb-3">
                  <h5>{doc.documentName}</h5>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() =>
                        handleDocumentView({
                          ...doc,
                          documentType: "SUPPORTING",
                        })
                      }
                    >
                      View Document
                    </Button>
                    <span
                      className={`badge ${doc.status === "PENDING" ? "bg-warning" : doc.status === "VERIFIED" ? "bg-success" : "bg-danger"}`}
                    >
                      {doc.status}
                    </span>
                  </div>
                  {doc.message && (
                    <p className="text-muted mt-2">{doc.message}</p>
                  )}
                </div>
              ))}
            </AccordionTab>

            <AccordionTab header="Company Legal Documents">
              {companyLegalDocuments.map((doc, index) => (
                <div key={doc._id} className="mb-3">
                  <h5>{doc.documentName}</h5>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() =>
                        handleDocumentView({ ...doc, documentType: "LEGAL" })
                      }
                    >
                      View Document
                    </Button>
                    <span
                      className={`badge ${doc.status === "PENDING" ? "bg-warning" : doc.status === "VERIFIED" ? "bg-success" : "bg-danger"}`}
                    >
                      {doc.status}
                    </span>
                  </div>
                  {doc.message && (
                    <p className="text-muted mt-2">{doc.message}</p>
                  )}
                </div>
              ))}
            </AccordionTab>
          </Accordion>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      <Toast ref={toast} position="top-right" life="3000" />
      {renderDocumentModal()}
      <div className="view-app-user">
        {loading && (
          <div className="loader-overlay">
            <ProgressSpinner />
          </div>
        )}

        <Row>
          <Col xs="12">
            <div className="page-header">
              <div className="d-flex flex-column gap-2">
                <CustomBreadCrumb
                  data={breadcrumbItems}
                  pageTitle="Verify Business User"
                  handleClick={handleBreadcrumbClick}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                height: "calc(100vh - 150px)",
                gap: "15px",
              }}
            >
              {/* Sidebar */}
              <div style={{ flex: "0 0 33.333%" }}>
                <div style={{ height: "100%" }}>{renderSidebar()}</div>
              </div>

              {/* Main Content */}
              <div style={{ flex: "0 0 66.667%" }}>
                <Card className="h-100">
                  <CardBody style={{ height: "100%", overflowY: "auto" }}>
                    {renderTabContent()}
                  </CardBody>
                </Card>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default VerifyBusinessUser
